import styled from 'styled-components'
import { Spacings, Text, Devices, COLORS } from '@styles'
import { Button } from '@baseV2'
import { ErrorMessage } from '@baseV2/styled'
import { H4 } from '@base'

export const TrackerWrapper = styled.div`
	position: relative;
	width: 100%;
	padding: ${Spacings.SPACING_4B} 0;
`

export const CircleRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	position: relative;
	width: 100%;
`

export const Track = styled.div`
	position: absolute;
	top: 50%;
	left: calc(${Spacings.SPACING_9B} / 2);
	right: calc(${Spacings.SPACING_9B} / 2);
	height: ${Spacings.SPACING_6};
	background: ${({ theme }) => theme.palette.background.greyBorder};
	border-radius: ${Spacings.SPACING_5};
	transform: translateY(-50%);
	z-index: 0;
`

export const Fill = styled.div`
	position: absolute;
	top: 50%;
	left: calc(${Spacings.SPACING_9B} / 2);
	height: ${Spacings.SPACING_6};
	background: ${({ theme }) => theme.palette.background.vehicleGreen};
	border-radius: ${Spacings.SPACING_5};
	transform: translateY(-50%);
	z-index: 0;
	width: calc((100% - ${Spacings.SPACING_9B}) * ${({ $percent }) => $percent} / 100);
	transition: width 600ms ease;
`

export const Circle = styled.div`
	width: ${Spacings.SPACING_9B};
	height: ${Spacings.SPACING_9B};
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	z-index: 1;
	background: ${({ done, theme }) => (done ? theme.palette.background.vehicleGreen : theme.palette.background.white)};
	border: 1px solid ${({ done, theme }) => (done ? theme.palette.background.vehicleGreen : theme.palette.background.greyBorder)};
	color: ${({ done, theme }) => (done ? theme.palette.text.white : theme.palette.text.greydark)};

	& > svg {
		font-size: ${Text.LARGE};
	}
`

export const LabelRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-top: ${Spacings.SPACING_2B};
	position: relative;
	min-height: ${Spacings.SPACING_11B};
	overflow: visible;
`

export const StepMeta = styled.div`
	display: flex;
	flex-direction: column;
	flex: 0 0 auto;
	max-width: 30%;
	align-items: ${({ $position }) => ($position === 'start' ? 'flex-start' : $position === 'end' ? 'flex-end' : 'center')};
`

export const StepLabel = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_600};
	white-space: nowrap;
`

export const StepDate = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.palette.text.greydark};
	white-space: nowrap;
`

/*
 * TransitCircle and TransitLabel share identical clamp bounds so the label
 * always renders directly below the icon — no visual misalignment.
 *
 * Circle leading-edge clamp:
 *   min = SPACING_10B (40 px) — 4 px clear of the 36 px Loaded circle
 *   max = 100% - SPACING_9B - SPACING_10B (76 px) — leaves room for TransitCircle + Delivered circle
 *
 * Label centre clamp  (leading-edge + half-circle radius):
 *   min = SPACING_9B/2 + SPACING_10B (58 px)
 *   max = 100% - SPACING_9B/2 - SPACING_10B (100% - 58 px)
 */
export const TransitCircle = styled.div`
	position: absolute;
	left: clamp(
		${Spacings.SPACING_10B},
		calc((100% - ${Spacings.SPACING_10B}) * ${({ $percent }) => $percent} / 100),
		calc(100% - ${Spacings.SPACING_10B} - ${Spacings.SPACING_10B})
	);
	top: 50%;
	transform: translateY(-50%);
	width: ${Spacings.SPACING_9B};
	height: ${Spacings.SPACING_9B};
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	cursor: pointer;
	z-index: 2;
	background: ${({ done, theme }) => (done ? theme.palette.background.vehicleGreen : theme.palette.background.white)};
	border: 2px solid ${({ done, theme }) => (done ? theme.palette.background.vehicleGreen : theme.palette.background.greyBorder)};
	color: ${({ done, theme }) => (done ? theme.palette.text.white : theme.palette.text.greydark)};
	transition: left 400ms ease;

	& > svg {
		font-size: ${Text.LARGE};
	}
`

export const TransitTooltip = styled.div`
	position: absolute;
	top: ${({ $position }) => ($position === 'bottom' ? `calc(100% + ${Spacings.SPACING_2B})` : 'auto')};
	bottom: ${({ $position }) => ($position === 'bottom' ? 'auto' : `calc(100% + ${Spacings.SPACING_2B})`)};
	left: 50%;
	transform: translateX(-50%);
	background: ${({ theme }) => theme.palette.text.black};
	color: ${({ theme }) => theme.palette.text.white};
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_10};
	border-radius: ${Spacings.SPACING_6};
	white-space: nowrap;
	font-size: ${Text.EXTRA_SMALL};
	opacity: 0;
	pointer-events: none;
	z-index: 10;
	transition: opacity 150ms ease;

	&::after {
		content: '';
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		border: ${Spacings.SPACING_6} solid transparent;
		top: ${({ $position }) => ($position === 'bottom' ? 'auto' : '100%')};
		bottom: ${({ $position }) => ($position === 'bottom' ? '100%' : 'auto')};
		border-top-color: ${({ $position, theme }) => ($position === 'bottom' ? 'transparent' : theme.palette.text.black)};
		border-bottom-color: ${({ $position, theme }) => ($position === 'bottom' ? theme.palette.text.black : 'transparent')};
	}

	${TransitCircle}:hover & {
		opacity: 1;
	}

	/* On narrow screens allow the tooltip to wrap so it stays within the viewport */
	@media not all and ${Devices.mobile} {
		white-space: normal;
		max-width: 150px;
		text-align: center;
	}
`

export const TransitLabel = styled.div`
	position: absolute;
	left: clamp(
		calc(${Spacings.SPACING_9B} / 2 + ${Spacings.SPACING_10B}),
		calc((100% - ${Spacings.SPACING_9B}) * ${({ $percent }) => $percent} / 100 + ${Spacings.SPACING_9B} / 2),
		calc(100% - ${Spacings.SPACING_9B} / 2 - ${Spacings.SPACING_10B})
	);
	transform: translateX(-50%);
	display: flex;
	flex-direction: column;
	align-items: center;
	transition: left 400ms ease;
	pointer-events: none;
	white-space: nowrap;
`

export const TransitCoveredLabel = styled.div`
	position: absolute;
	bottom: calc(100% + ${Spacings.SPACING_2B});
	left: 50%;
	transform: translateX(-50%);
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.palette.text.greydark};
	white-space: nowrap;
	text-align: center;
`

export const TooltipRow = styled.span`
	display: block;
	line-height: 1.6;

	b {
		font-weight: ${Text.BOLD_600};
	}
`

export const BasicDetailsCard = styled.div`
	background: ${({ theme }) => theme.palette.background.white};
	border: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	border-radius: ${Spacings.SPACING_5};
	padding: ${Spacings.SPACING_4B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
	width: 100%;
`

export const CardHeader = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${Spacings.SPACING_4B};
	align-items: center;
	justify-content: space-between;
	${({ $noBorder, theme }) =>
		!$noBorder &&
		`
		border-bottom: 1px solid ${theme.palette.background.greyBorder};
		padding-bottom: ${Spacings.SPACING_10};
	`}
`

export const CardTitle = styled(H4)`
	font-weight: ${Text.BOLD_600};
`

export const HeaderRight = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_10};
`
export const ExportDocumentsWrapper = styled.div`
	display: flex;
	width: 100%;
	align-items: center;
`

export const EditBtn = styled(Button).attrs({ variant: 'ghost' })`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_5};
	background: none;
	border: none;
	cursor: pointer;
	color: ${COLORS.blue.default};
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_400};
	padding: 0;

	&:hover:not(:disabled) {
		background-color: transparent;
	}

	& > svg {
		font-size: ${Text.LARGE};
	}
`

export const ChevronBtn = styled(Button).attrs({ variant: 'ghost' })`
	background: none;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${Spacings.SPACING_6B};
	height: ${Spacings.SPACING_6B};
	padding: 0;
	color: ${({ theme }) => theme.palette.text.black};
`

export const DetailGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_10};
	width: 100%;
`

export const SectionDivider = styled.div`
	height: 1px;
	background: ${({ theme }) => theme.palette.background.greyBorder};
`

export const DetailRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding-top: ${({ $editMode }) => ($editMode ? Spacings.SPACING_6 : 0)};
	transition: padding 0.2s ease;
`

export const DetailLabel = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_400};
	color: ${({ theme }) => theme.palette.text.greydark};
`

export const DetailValue = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.palette.text.blackGrey};
`

export const FieldRight = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	min-width: 55%;
	max-width: 65%;
`

export const FieldError = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.palette.text.messageRed};
	margin-top: ${Spacings.SPACING_2};
`

export const EditControlWrapper = styled.div`
	width: 100%;
`

export const TrackingLinkRow = styled.div`
	display: flex;
	align-items: center;
`

export const TrackingLinkValue = styled.a`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${COLORS.blue.default};
	text-decoration: underline;
	word-break: break-all;
	cursor: pointer;
`

export const VehicleHeaderTitleRow = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_6};
`

export const StatusChip = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_500};
	padding: ${Spacings.SPACING_2} ${Spacings.SPACING_6};
	border-radius: ${Spacings.SPACING_10};
	background: ${({ $variant, theme }) =>
		$variant === 'transit' || $variant === 'delivered'
			? theme.palette.background.greenpill
			: $variant === 'expired'
				? theme.palette.background.orangeLightest
				: theme.palette.background.greyBorder};
	color: ${({ $variant, theme }) =>
		$variant === 'transit' || $variant === 'delivered'
			? theme.palette.text.Fog
			: $variant === 'expired'
				? theme.palette.background.orangeLight
				: theme.palette.text.greydark};
`

export const VehicleInputSection = styled.div`
	background: ${({ theme }) => theme.palette.background.main};
	border-radius: ${Spacings.SPACING_5};
	padding: ${Spacings.SPACING_4B};
	display: flex;
	align-items: flex-end;
	gap: ${Spacings.SPACING_4B};
`

export const VehicleInputGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_5};
	flex: 1;
	min-width: 0;
	position: relative;

	${ErrorMessage} {
		position: absolute;
		top: 100%;
		left: 0;
		margin-top: ${Spacings.SPACING_1};
		white-space: nowrap;
	}
`

export const VehicleInputLabel = styled.span`
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.palette.text.greydark};
`

export const VehicleActionButtons = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_6};
	flex-shrink: 0;
`

export const IconActionBtn = styled(Button).attrs({ variant: 'ghost' })`
	background: none;
	border: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	width: ${Spacings.SPACING_8B};
	height: ${Spacings.SPACING_9B};
	padding: 0;
	color: ${({ theme }) => theme.palette.text.black};
	border-radius: ${Spacings.SPACING_5};
	min-width: unset;

	&:hover:not(:disabled) {
		background-color: transparent;
	}
`

export const MoreMenuWrapper = styled.div`
	position: relative;
`

export const MoreMenuList = styled.div`
	position: absolute;
	right: 0;
	top: calc(100% + ${Spacings.SPACING_5});
	background: ${({ theme }) => theme.palette.background.white};
	border: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	border-radius: ${Spacings.SPACING_5};
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	min-width: 150px;
	z-index: 20;
	overflow: hidden;
`

export const MoreMenuItemStyled = styled.button`
	display: block;
	width: 100%;
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_4B};
	background: none;
	border: none;
	text-align: left;
	font-size: ${Text.SMALL};
	color: ${({ $danger, theme }) => ($danger ? theme.palette.text.red : theme.palette.text.black)};
	cursor: pointer;

	&:hover {
		background: ${({ theme }) => theme.palette.background.main};
	}
`

export const PodInnerCard = styled.div`
	border: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	border-radius: ${Spacings.SPACING_5};
	padding: ${Spacings.SPACING_4B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};
`

export const ViewDetailsLink = styled.span`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_500};
	color: ${COLORS.blue.default};
	cursor: pointer;
	margin-left: ${Spacings.SPACING_5};
`

export const PodHeaderRight = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_6};
`

export const ModalTitle = styled.h5`
	font-size: ${Text.LARGE};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.palette.text.darkblack};
	padding-bottom: ${Spacings.SPACING_3B};
	border-bottom: 1px solid ${({ theme }) => theme.palette.background.greyBorder};
	margin: 0 0 ${Spacings.SPACING_4B};
`

export const ModalBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
	padding-bottom: 8rem;
`

export const MobileRow = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
	align-items: flex-end;
`

export const MobileFieldGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2};
`

export const CountryCodeWrapper = styled.div`
	width: 5rem;
	flex-shrink: 0;
`

export const ModalActions = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
	padding-top: ${Spacings.SPACING_4B};
	justify-content: flex-end;
	position: sticky;
	bottom: 0;
	background: ${({ theme }) => theme.palette.background.white};
`

export const FieldGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_2};
`

export const FieldErrorText = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.palette.text.messageRed};
`
